/**
 * Was die Seite über ihre Live-Mitschrift weiß — eine Angabe für Browser und Server.
 * (12.10.2026, Bahn live-primaer)
 *
 * ## Woher das kommt
 *
 * Die Live-Mitschrift (Soniox, parallel zur Aufnahme) ist beim Absenden meist schon fertig, galt aber
 * nur als Zeuge und Rückfall. Soll sie den Text liefern dürfen, muss der Server wissen, ob sie
 * VOLLSTÄNDIG war — und das weiß nur die Seite: ob Soniox den Abschluss bestätigt hat, ob die
 * Verbindung mitten in der Aufnahme riss, wie viel Ton überhaupt ankam. Das reist als EIN Feld
 * (`liveStand`, JSON) neben dem Text mit.
 *
 * ## Vertrauen
 *
 * Alles hier ist Eingabe des Browsers, also nicht vertrauenswürdig: `leseLiveStand` prüft Typ und
 * Wertebereich und kennt keine weiteren Felder. Der Server verlässt sich nicht allein darauf — die
 * Dauer der Aufnahme misst er selbst an der Tondatei (server/live-primaer.ts), und der Stand
 * entscheidet nur, ob der bisherige Weg (Datei-Erkennung) ENTFÄLLT, nie über Rechte.
 *
 * ⚠️ Nur Zahlen und Ja/Nein — nie ein Wort aus dem Gesagten.
 */

export interface LiveStand {
  /** Hat Soniox den Abschluss bestätigt (`finished`), bevor gesendet wurde? Sonst fehlt vielleicht das Ende. */
  abschluss: boolean;
  /** Riss die Verbindung (Fehler oder Schluss) MITTEN in der Aufnahme? Dann fehlt ein Stück. */
  abbruch: boolean;
  /** So viel Ton (Millisekunden) ist bei Soniox angekommen — Bytes gesendet, nicht Sprache gezählt. */
  tonMs: number;
  /** So viel Ton ging verloren, weil die Leitung noch nicht stand und der Puffer voll war (ms). */
  verlorenMs: number;
  /** Wie lange die Seite nach dem Loslassen auf den Abschluss gewartet hat (ms), `null`: unbekannt. */
  abschlussMs: number | null;
  /**
   * Wo auf der Tonachse das LETZTE bestätigte Wort endet (ms, `end_ms` von Soniox), `null`: kein Wort / unbekannt.
   * Liegt es weit vor dem Ende des gesendeten Tons, fehlt der Schluss — oder dort wurde nur geschwiegen
   * (server/live-primaer.ts, Regel „ende“).
   */
  endeLetztesWortMs: number | null;
  /**
   * Mittlere Zuversicht der Erkennung über alle bestätigten Wörter (0…1, je Wort die kleinste ihrer Teilstücke),
   * `null`: Soniox nannte keine. Echte Rede liegt um 0,93 (Journal 05.–12.10.), Rauschen darunter.
   */
  konfidenzMittel: number | null;
}

/** Obergrenze für jede Zeitangabe: 24 Stunden. Alles darüber ist Unsinn, nicht Grenze. */
const MAX_MS = 24 * 60 * 60 * 1000;

/** Das Feld ist klein; alles Längere ist kein `LiveStand`. */
const MAX_FELD_ZEICHEN = 400;

function zeit(v: unknown): number | null {
  if (typeof v !== "number" || !Number.isFinite(v)) return null;
  if (v < 0 || v > MAX_MS) return null;
  return Math.round(v);
}

function anteil(v: unknown): number | null {
  if (typeof v !== "number" || !Number.isFinite(v)) return null;
  if (v < 0 || v > 1) return null;
  return Math.round(v * 1000) / 1000;
}

function ja(v: unknown): boolean | null {
  if (v === true || v === 1) return true;
  if (v === false || v === 0) return false;
  return null;
}

/**
 * Das Feld der Anfrage lesen. `null` heißt: keines oder unbrauchbar — der Server nimmt dann den
 * bisherigen Weg. Wirft nie.
 */
export function leseLiveStand(roh: unknown): LiveStand | null {
  if (typeof roh !== "string" || roh.length === 0 || roh.length > MAX_FELD_ZEICHEN) return null;
  let o: unknown;
  try {
    o = JSON.parse(roh);
  } catch {
    return null;
  }
  if (typeof o !== "object" || o === null || Array.isArray(o)) return null;
  const f = o as Record<string, unknown>;
  const abschluss = ja(f.abschluss);
  const abbruch = ja(f.abbruch);
  const tonMs = zeit(f.tonMs);
  const verlorenMs = zeit(f.verlorenMs);
  if (abschluss === null || abbruch === null || tonMs === null || verlorenMs === null) return null;
  return {
    abschluss,
    abbruch,
    tonMs,
    verlorenMs,
    abschlussMs: zeit(f.abschlussMs),
    endeLetztesWortMs: zeit(f.endeLetztesWortMs),
    konfidenzMittel: anteil(f.konfidenzMittel),
  };
}

/** Das Feld für die Anfrage (Browser). */
export function liveStandAlsFeld(s: LiveStand): string {
  return JSON.stringify({
    abschluss: s.abschluss ? 1 : 0,
    abbruch: s.abbruch ? 1 : 0,
    tonMs: Math.max(0, Math.round(s.tonMs)),
    verlorenMs: Math.max(0, Math.round(s.verlorenMs)),
    ...(s.abschlussMs === null ? {} : { abschlussMs: Math.max(0, Math.round(s.abschlussMs)) }),
    ...(s.endeLetztesWortMs === null ? {} : { endeLetztesWortMs: Math.max(0, Math.round(s.endeLetztesWortMs)) }),
    ...(s.konfidenzMittel === null ? {} : { konfidenzMittel: Math.min(1, Math.max(0, Math.round(s.konfidenzMittel * 1000) / 1000)) }),
  });
}
